import React, { useRef, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle2, Mail, Phone, Upload, ImageIcon, CheckCheck } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const OrderSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const orderId = searchParams.get('order_id');
  const isOnlinePayment = searchParams.get('payment_method') === 'online';

  const [uploading, setUploading] = useState(false);
  const [uploaded, setUploaded] = useState(false);
  const [preview, setPreview] = useState(null);
  const fileRef = useRef(null);

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file.');
      return;
    }
    setPreview(URL.createObjectURL(file));
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      await axios.post(`${API}/orders/${orderId}/payment-screenshot`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setUploaded(true);
      toast.success('Payment screenshot sent! The store will verify it shortly.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed. Please try again.');
      setPreview(null);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 px-4" data-testid="order-success-page">
      <Card className="w-full max-w-md">
        <CardContent className="flex flex-col items-center py-12">
          <div className="relative mb-6">
            <div className="absolute inset-0 bg-green-500/20 rounded-full blur-2xl"></div>
            <CheckCircle2 className="relative h-20 w-20 text-green-600 animate-scale" />
          </div>

          <h2 className="text-3xl font-bold mb-3 bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
            Order Placed! 🎉
          </h2>

          <p className="text-center text-muted-foreground mb-6 max-w-sm">
            Thank you for your order! The store has been notified and will confirm it shortly on WhatsApp.
          </p>

          {orderId && (
            <div className="w-full bg-secondary/50 rounded-lg p-4 mb-6">
              <p className="text-sm text-muted-foreground text-center">Order ID</p>
              <p className="text-lg font-mono font-bold text-center mt-1">
                #{orderId.slice(0, 8).toUpperCase()}
              </p>
            </div>
          )}

          {/* Payment screenshot upload — only for online payments */}
          {isOnlinePayment && orderId && (
            <div className="w-full mb-6">
              <div className="rounded-xl border-2 border-dashed border-green-300 bg-green-50 p-4">
                <div className="flex items-start gap-3 mb-3">
                  <ImageIcon className="h-5 w-5 text-green-600 mt-0.5 shrink-0" />
                  <div>
                    <h3 className="font-semibold text-sm text-green-900">Upload Payment Screenshot</h3>
                    <p className="text-xs text-green-700 mt-0.5">
                      Share your payment confirmation so the store can verify it quickly.
                    </p>
                  </div>
                </div>

                {preview && (
                  <div className="mb-3 rounded-lg overflow-hidden border border-green-200">
                    <img src={preview} alt="Payment screenshot" className="w-full max-h-48 object-contain bg-white" />
                  </div>
                )}

                {uploaded ? (
                  <div className="flex items-center gap-2 text-green-700 font-semibold text-sm">
                    <CheckCheck className="h-4 w-4" /> Screenshot sent successfully!
                  </div>
                ) : (
                  <>
                    <input
                      ref={fileRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleFileChange}
                    />
                    <Button
                      variant="outline"
                      className="w-full border-green-400 text-green-800 hover:bg-green-100"
                      onClick={() => fileRef.current?.click()}
                      disabled={uploading}
                    >
                      <Upload className="h-4 w-4 mr-2" />
                      {uploading ? 'Uploading…' : 'Choose screenshot'}
                    </Button>
                  </>
                )}
              </div>
            </div>
          )}

          <div className="w-full space-y-4 mb-6">
            <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <Mail className="h-5 w-5 text-blue-600 mt-0.5" />
              <div>
                <h3 className="font-semibold text-sm text-blue-900">Order sent to the store</h3>
                <p className="text-xs text-blue-700 mt-1">
                  You'll get a WhatsApp confirmation on your number once the store confirms your order.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3 p-4 bg-purple-50 rounded-lg border border-purple-200">
              <Phone className="h-5 w-5 text-purple-600 mt-0.5" />
              <div>
                <h3 className="font-semibold text-sm text-purple-900">What's Next?</h3>
                <p className="text-xs text-purple-700 mt-1">
                  The vendor has received your order and will contact you soon to confirm and arrange delivery.
                </p>
              </div>
            </div>
          </div>

          <div className="flex gap-3 w-full">
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => navigate('/')}
              data-testid="back-home-btn"
            >
              Back to Home
            </Button>
            <Button
              className="flex-1 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white"
              onClick={() => navigate(-2)}
              data-testid="continue-shopping-btn"
            >
              Continue Shopping
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default OrderSuccess;
